import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useReport,
  useRegenerateReport,
  useUpdateReport,
  useSendReport,
} from '../../hooks/useMarketing';
import { REPORT_STATUS_STYLES } from './Reports';
import QuoteEditor from './QuoteEditor';

const SECTION_TITLES = {
  overview: 'Overview',
  markAssessment: 'Our Assessment of the Mark',
  registerFindings: 'Register Findings',
  coverageRationale: 'Protection Coverage',
  ourRecommendation: 'Our Recommendation',
  nextSteps: 'Next Steps',
};

/**
 * ReportDetail — review, edit and send a generated report.
 * - Sections editable per block (saved to the reports row)
 * - Register matches table (real IP Australia hits)
 * - Quote card (QuoteEditor): countries, classes and price, and revising the
 *   quote after the client already has it
 * - Send mints a fresh Clerk magic link server-side and emails the report
 */
function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useReport(id);
  const regenerate = useRegenerateReport();
  const updateReport = useUpdateReport();
  const sendReport = useSendReport();

  const report = data?.report || null;
  const application = data?.application || null;

  const [sections, setSections] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Rehydrate section text from the server copy, but never while a section
  // is being edited — a refetch (e.g. after a quote save invalidates the
  // query) must not wipe in-progress edits.
  useEffect(() => {
    if (report?.sections && !editingSection) setSections(report.sections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.sections]);

  if (isLoading) {
    return <div className="mkt-loading"><div className="mkt-loading-spinner" /><p>Loading report...</p></div>;
  }
  if (error || !report) {
    return (
      <div className="mkt-page">
        <p style={{ color: '#dc2626' }}>Report not found.</p>
        <Link to="/marketing/reports">Back to reports</Link>
      </div>
    );
  }

  const { label: statusLabel, ...statusStyle } = REPORT_STATUS_STYLES[report.status] || {};
  const quote = report.quote || null;
  const rs = report.registerSearch || null;
  const matches = rs ? [...(rs.exactMatches || []), ...(rs.nearMatches || [])] : [];

  const saveSection = (key) => {
    updateReport.mutate(
      { reportId: report.id, sections: { [key]: sections[key] } },
      { onSuccess: () => setEditingSection(null), onError: (e) => setActionError(e.message) }
    );
  };

  const doSend = () => {
    setActionError(null);
    sendReport.mutate(
      { reportId: report.id },
      {
        onSuccess: () => {
          setConfirmSend(false);
          navigate('/marketing/reports');
        },
        onError: (e) => {
          setConfirmSend(false);
          setActionError(e.response?.data?.error || e.message);
        },
      }
    );
  };

  return (
    <div className="mkt-page">
      <div className="mkt-page-header">
        <div>
          <Link to="/marketing/reports" style={{ fontSize: '13px', color: '#0f766e' }}>&larr; Reports</Link>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {report.trademarkName ? `"${report.trademarkName}"` : report.applicationId}
            <span style={{ ...statusStyle, borderRadius: '4px', fontSize: '12px', fontWeight: 700, padding: '2px 10px' }}>
              {statusLabel || report.status}
            </span>
          </h1>
          <p className="mkt-page-subtitle">
            {report.contact?.name || ''} {report.contact?.email ? `· ${report.contact.email}` : ''}
            {report.contact?.phone ? ` · ${report.contact.phone}` : ''}
            {application?.status ? ` · application: ${application.status}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <button
            className="mkt-btn mkt-btn-secondary"
            disabled={regenerate.isPending || report.status === 'generating'}
            onClick={() => regenerate.mutate(report.id, { onError: (e) => setActionError(e.message) })}
          >
            {regenerate.isPending ? 'Regenerating...' : 'Regenerate'}
          </button>
          {report.status === 'sent' ? (
            <button
              className="mkt-btn mkt-btn-primary"
              disabled={sendReport.isPending}
              onClick={() => setConfirmSend(true)}
            >
              {sendReport.isPending ? 'Sending...' : 'Resend with fresh link'}
            </button>
          ) : (
            <button
              className="mkt-btn mkt-btn-primary"
              disabled={sendReport.isPending || report.status !== 'draft'}
              onClick={() => setConfirmSend(true)}
            >
              {sendReport.isPending ? 'Sending...' : 'Send report & quote'}
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
          {actionError}
        </div>
      )}
      {report.error && report.status === 'failed' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
          Generation failed: {report.error}
        </div>
      )}
      {report.provisioningError && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#b45309', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
          Account provisioning failed at quote request ({report.provisioningError}). Sending will retry provisioning automatically.
        </div>
      )}

      {/* Sections */}
      {Object.keys(SECTION_TITLES).map((key) => (
        <div key={key} className="mkt-card" style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569' }}>
              {SECTION_TITLES[key]}
            </h3>
            {editingSection === key ? (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="mkt-btn mkt-btn-secondary" onClick={() => { setSections(report.sections || {}); setEditingSection(null); }}>Cancel</button>
                <button className="mkt-btn mkt-btn-secondary" style={{ color: '#0f766e', fontWeight: 600 }} disabled={updateReport.isPending} onClick={() => saveSection(key)}>
                  {updateReport.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <button className="mkt-btn mkt-btn-secondary" onClick={() => setEditingSection(key)}>Edit</button>
            )}
          </div>
          {editingSection === key ? (
            <textarea
              value={sections[key] || ''}
              onChange={(e) => setSections((prev) => ({ ...prev, [key]: e.target.value }))}
              rows={5}
              style={{ width: '100%', marginTop: '8px', fontSize: '14px', lineHeight: 1.6, padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
            />
          ) : (
            <p style={{ margin: '8px 0 0', color: '#334155', fontSize: '14px', lineHeight: 1.6 }}>
              {sections[key] || <em style={{ color: '#94a3b8' }}>Empty</em>}
            </p>
          )}
        </div>
      ))}

      {/* Register matches */}
      <div className="mkt-card" style={{ marginBottom: '10px' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569' }}>
          IP Australia Register Search
          {rs?.checkedAt && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}> · checked {new Date(rs.checkedAt).toLocaleString()}</span>}
        </h3>
        {!rs || rs.unavailable ? (
          <p style={{ color: '#b45309', fontSize: '13px', margin: 0 }}>
            Register search was unavailable{rs?.error ? ` (${rs.error})` : ''}. Regenerate to retry.
          </p>
        ) : matches.length === 0 ? (
          <p style={{ color: '#047857', fontSize: '13px', margin: 0 }}>No matches found on the register.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Mark</th>
                <th style={{ padding: '6px 8px' }}>Number</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}>Owner</th>
                <th style={{ padding: '6px 8px' }}>Classes</th>
                <th style={{ padding: '6px 8px' }}>Match</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, i) => {
                const exact = (rs.exactMatches || []).includes(m);
                return (
                  <tr key={`${m.number || i}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{m.name}</td>
                    <td style={{ padding: '6px 8px', color: '#64748b' }}>{m.number}</td>
                    <td style={{ padding: '6px 8px', color: '#64748b' }}>{m.status}</td>
                    <td style={{ padding: '6px 8px', color: '#64748b' }}>{m.ownerName}</td>
                    <td style={{ padding: '6px 8px', color: '#64748b' }}>{(m.classes || []).join(', ')}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{ background: exact ? '#fef2f2' : '#f1f5f9', color: exact ? '#dc2626' : '#64748b', borderRadius: '4px', fontSize: '11px', fontWeight: 600, padding: '1px 6px' }}>
                        {exact ? 'exact' : 'near'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Quote — scope and money, plus revising it after the client has it */}
      <QuoteEditor report={report} quote={quote} onError={setActionError} />

      {/* Send confirmation */}
      {confirmSend && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '440px', width: '90%' }}>
            <h3 style={{ margin: '0 0 8px' }}>Send report &amp; quote?</h3>
            <p style={{ color: '#475569', fontSize: '14px' }}>
              This emails the report to <strong>{report.contact?.email}</strong> with a fresh
              dashboard sign-in link, confirms the quote, and moves the pipeline to Report Sent.
            </p>
            {quote?.quoteStatus !== 'confirmed' && (
              <p style={{ color: '#b45309', fontSize: '13px' }}>
                The quote is still an estimate. It will be marked confirmed as-is when sent.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button className="mkt-btn mkt-btn-secondary" onClick={() => setConfirmSend(false)}>Cancel</button>
              <button
                className="mkt-btn mkt-btn-primary"
                disabled={sendReport.isPending}
                onClick={doSend}
              >
                {sendReport.isPending ? 'Sending...' : 'Send it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportDetail;
